import { storeToRefs } from 'pinia'
import { nanoid } from 'nanoid'
import {useMainStore, useSlidesStore, useWidgetStore} from '@/store'
import { getImageSize } from '@/utils/image'
import { VIEWPORT_SIZE } from '@/configs/canvas'
import {
  PPTLineElement,
  PPTElement,
  TableCell,
  TableCellStyle,
  PPTShapeElement,
  PPTSectionElement,
  ChartOptions,
  PresetChartType,
  PPTSlideLayoutElement, PPTElementOutline, PPTElementShadow
} from '@/types/slides'
import { ShapePoolItem, SHAPE_PATH_FORMULAS } from '@/configs/shapes'
import { LinePoolItem } from '@/configs/lines'
import { CHART_TYPES } from '@/configs/chartTypes'
import useHistorySnapshot from '@/hooks/useHistorySnapshot'
import useMicroservice from '@/hooks/useMicroservice'

interface CommonElementPosition {
  top: number
  left: number
  width: number
  height: number
}

interface LineElementPosition {
  top: number
  left: number
  start: [number, number]
  end: [number, number]
}

export default () => {
  const mainStore = useMainStore()
  const slidesStore = useSlidesStore()
  const { creatingElement } = storeToRefs(mainStore)
  const { theme, viewportRatio } = storeToRefs(slidesStore)
  const api = useMicroservice()

  const { addHistorySnapshot } = useHistorySnapshot()
  const widgetStore = useWidgetStore()
  const {
    selectedDragDropId,
    selectedSectionId,
    selectedSectionRowId,
    selectedSubsectionId,
    selectedSubsectionRowId,
  } = storeToRefs(widgetStore)

  // Create (insert) an element and set it as the selected element
  const createElement = async (element: PPTElement, callback?: () => void) => {
    slidesStore.addElement(element)
    mainStore.setActiveElementIdList([element.id])

    if (creatingElement.value) mainStore.setCreatingElement(null)

    setTimeout(() => {
      mainStore.setEditorareaFocus(true)
    }, 0)

    if (callback) callback()

    addHistorySnapshot()
    await api.savePresentation()
  }


  /**
   * Create image elements
   * @param src The map's address
   */
  const createImageElement = (src: string) => {
    getImageSize(src).then(({ width, height }) => {
      const scale = height / width

      if (scale < viewportRatio.value && width > VIEWPORT_SIZE) {
        width = VIEWPORT_SIZE
        height = width * scale
      }
      else if (height > VIEWPORT_SIZE * viewportRatio.value) {
        height = VIEWPORT_SIZE * viewportRatio.value
        width = height / scale
      }

      createElement({
        type: 'image',
        id: nanoid(10),
        src,
        width,
        height,
        left: (VIEWPORT_SIZE - width) / 2,
        top: (VIEWPORT_SIZE * viewportRatio.value - height) / 2,
        fixedRatio: true,
        rotate: 0,
      })
    })
  }

  const createSectionElement = (type: PresetChartType) => {

    const layout = createSlideLayout()

    const newElement: PPTSectionElement = {
      type: 'section',
      id: nanoid(10),
      chartType: CHART_TYPES[type],
      slideLayoutId: layout.id,
      left: 300,
      top: 81.25,
      width: 400,
      height: 400,
      rotate: 0,
      themeColor: [theme.value.themeColor],
      gridColor: theme.value.fontColor,
      data: {
        labels: ['category 1', 'category 2', 'category 3', 'category 4', 'category 5'],
        legends: ['Series 1'],
        series: [
          [
            { meta: 'category 1', value: 12 },
            { meta: 'category 2', value: 19 },
            { meta: 'category 3', value: 5 },
            { meta: 'category 4', value: 2 },
            { meta: 'category 5', value: 18 }
          ]
        ],
        hookIds: [
          []
        ]
      },
      labelStyle: {
        fontSize: '12px',
        fill: '#000'
      }
    }


    let options: ChartOptions = {}
    if (type === 'horizontalBar') options = { horizontalBars: true }
    else if (type === 'area') options = { showArea: true }
    else if (type === 'scatter') options = { showLine: false }
    else if (type === 'ring') options = { donut: true }

    createElement({
      ...newElement,
      options,
    })
  }

  const createSlideLayout = () => {
    const rowJson = {
      'type': 'row',
      'children': [
        {
          'id': 1
        }
      ]
    }
    const slideLayoutElementId = createSlideLayoutElement(false)

    const tempSubsectionId = nanoid(10)
    const tempSubsectionRowId = nanoid(10)
    const tempSectionRowId = nanoid(10)
    const tempSectionId = nanoid(10)

    const _row = JSON.parse(JSON.stringify(rowJson))
    _row.id = tempSectionRowId

    const child = _row.children[0]
    child.id = tempSectionId
    child.style = {
      valign: 'center',
      align: 'center',
      justifyContent: 'center'
    }

    createElement({
      type: 'dragdrop',
      id: child.id,
      left: 0,
      top: 0,
      width: null,
      height: null,
      content: '',
      rotate: 0,
      defaultFontName: 'Arial',
      defaultColor: '',
      layout: [{
        'type': 'row',
        'id': tempSubsectionRowId,
        'children': [
          {
            'id': tempSubsectionId,
            'style': {
              valign: 'center',
              align: 'center',
              justifyContent: 'center',
              margin: 5
            }
          }
        ],
        'style': {
          'backgroundColor': 'transparent',
          'clipTop': 10,
          'clipRight': 10,
          'clipBottom': 10,
          'clipLeft': 10,
          'borderRadius': 10,
          valign: 'center',
          align: 'center',
          justifyContent: 'center'
        }
      }],
      widgets: []
    })


    const slideLayoutElement: PPTSlideLayoutElement = <PPTSlideLayoutElement>slidesStore.getElementsByType('slidelayout').find(item => item.id === slideLayoutElementId)
    slideLayoutElement.layout.children.push(_row)

    slidesStore.updateElement({id: slideLayoutElement.id, props: {layout: slideLayoutElement.layout}})

    selectedSectionRowId.value = tempSectionRowId
    selectedSectionId.value = tempSectionId
    selectedSubsectionId.value = tempSubsectionId
    selectedSubsectionRowId.value = tempSubsectionRowId
    selectedDragDropId.value = tempSectionId

    api.savePresentation()
    addHistorySnapshot()
    return slideLayoutElement
  }



  /**
   * Create table elements
   * @param row Rows
   * @param col Number of columns
   */
  const createTableElement = (row: number, col: number) => {
    const style: TableCellStyle = {
      fontname: theme.value.fontName,
      color: theme.value.fontColor,
    }
    const data: TableCell[][] = []
    for (let i = 0; i < row; i++) {
      const rowCells: TableCell[] = []
      for (let j = 0; j < col; j++) {
        rowCells.push({ id: nanoid(10), colspan: 1, rowspan: 1, text: '', style })
      }
      data.push(rowCells)
    }

    const DEFAULT_CELL_WIDTH = 100
    const DEFAULT_CELL_HEIGHT = 36

    const colWidths: number[] = new Array(col).fill(1 / col)
    const rowHeights: number[] = new Array(col).fill(1 / row)

    const width = col * DEFAULT_CELL_WIDTH
    const height = row * DEFAULT_CELL_HEIGHT

    return {
      type: 'table',
      id: nanoid(10),
      width,
      height,
      colWidths,
      rotate: 0,
      data,
      // left: (VIEWPORT_SIZE - width) / 2,
      // top: (VIEWPORT_SIZE * viewportRatio.value - height) / 2,
      outline: {
        width: 2,
        style: 'solid',
        color: '#eeece1',
      },
      theme: {
        color: '#dbdbdb',
        rowHeader: true,
        rowFooter: false,
        colHeader: false,
        colFooter: false,
      },
      cellMinHeight: 36,
      rowHeights: rowHeights
    }
  }

  /**
   * Create text elements
   * @param position position size information
   * @param content text content
   */

  interface CreateTextData {
    content?: string
    vertical?: boolean
  }
  const createTextElement = (position: CommonElementPosition, data?: CreateTextData) => {
    const { left, top, width, height } = position
    const content = data?.content || ''
    const vertical = data?.vertical || false

    const id = nanoid(10)
    // createElement({
    //   type: 'text',
    //   id,
    //   left,
    //   top,
    //   width,
    //   height,
    //   content,
    //   rotate: 0,
    //   defaultFontName: theme.value.fontName,
    //   defaultColor: theme.value.fontColor,
    //   vertical,
    // }, () => {
    //   setTimeout(() => {
    //     const editorRef: HTMLElement | null = document.querySelector(`#editable-element-${id} .ProseMirror`)
    //     if (editorRef) editorRef.focus()
    //   }, 0)
    // })
  }

  /**
   * Create html elements
   * @param position position size information
   * @param content html content
   */

  interface CreateHtmlData {
    content?: string
  }
  const createHtmlElement = (type: string) => {
    const content = ''

    const width = 200
    const height = 50


    const left = (VIEWPORT_SIZE - width) / 2
    const top = (VIEWPORT_SIZE * viewportRatio.value - height) / 2


    const id = nanoid(10)
    createElement({
      type: 'html',
      id,
      left,
      top,
      width,
      height,
      content,
      rotate: 0,
      defaultFontName: theme.value.fontName,
      defaultColor: theme.value.fontColor,
    }, () => {
      setTimeout(() => {
        const editorRef: HTMLElement | null = document.querySelector(`#editable-element-${id} .ProseMirror`)
        if (editorRef) editorRef.focus()
      }, 0)
    })
  }

  /**
   * Create drag n drop elements
   * @param layoutIndex index of chosen layout
   */

  interface CreateDragDropData {
    layoutIndex?: number
  }

  const createDragDropElement = () => {
    // there can only be one drag/drop element per slide
    if (slidesStore.getElementsByType('dragdrop').length > 0) {
      return
    }

    const content = ''

    const width = 1366
    const height = 768 // this isn't working, setting it to 20

    const left = (VIEWPORT_SIZE - width) / 2
    const top = 0

    const id = nanoid(10)

    createElement({
      type: 'dragdrop',
      id,
      left,
      top,
      width,
      height,
      content,
      rotate: 0,
      defaultFontName: 'Arial',
      defaultColor: theme.value.fontColor,
      layout: [],
      widgets: []
    })
  }

  const createSlideLayoutElement = (mainSlideLayout = true) => {
    // there can only be one main slide layout per slide
    if (mainSlideLayout && slidesStore.getElementsByType('slidelayout').length > 0) {
      return
    }

    const content = ''

    const width = mainSlideLayout ? 1366: null
    const height = mainSlideLayout ? 768: null // this isn't working, setting it to 20

    const left = 0 //(VIEWPORT_SIZE - width) / 2
    const top = 0

    const id = nanoid(10)

   createElement({
      type: 'slidelayout',
      canvasHide: !mainSlideLayout,
      id,
      left,
      top,
      width,
      height,
      content,
      rotate: 0,
      defaultFontName: 'Arial',
      defaultColor: theme.value.fontColor,
      layout: {
        'type': 'layout',
        'children': []
      }
    })

    return id
  }

  /**
   * Create shape elements
   * @param position position size information
   * @param data Shape Path Information
   */
  const createShapeElement = (position: CommonElementPosition, data: ShapePoolItem) => {
    const { left, top, width, height } = position
    const newElement: PPTShapeElement = {
      type: 'shape',
      id: nanoid(10),
      left,
      top,
      width,
      height,
      viewBox: data.viewBox,
      path: data.path,
      fill: theme.value.themeColor,
      fixedRatio: false,
      rotate: 0,
    }
    if (data.special) newElement.special = true
    if (data.pathFormula) {
      newElement.pathFormula = data.pathFormula
      newElement.viewBox = [width, height]

      const pathFormula = SHAPE_PATH_FORMULAS[data.pathFormula]
      if ('editable' in pathFormula) {
        newElement.path = pathFormula.formula(width, height, pathFormula.defaultValue)
        newElement.keypoint = pathFormula.defaultValue
      }
      else newElement.path = pathFormula.formula(width, height)
    }
    createElement(newElement)
  }

  /**
   * Create line elements
   * @param position position size information
   * @param data The path and style of the line
   */
  const createLineElement = (position: LineElementPosition, data: LinePoolItem) => {
    const { left, top, start, end } = position

    const newElement: PPTLineElement = {
      type: 'line',
      id: nanoid(10),
      left,
      top,
      start,
      end,
      points: data.points,
      color: theme.value.themeColor,
      style: data.style,
      width: 2,
    }
    if (data.isBroken) newElement.broken = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2]
    if (data.isCurve) newElement.curve = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2]
    if (data.isCubic) newElement.cubic = [[(start[0] + end[0]) / 2, (start[1] + end[1]) / 2], [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2]]
    createElement(newElement)
  }

  /**
   * Create LaTeX elements
   * @param svg SVG code
   */
  const createLatexElement = (data: { path: string; latex: string; w: number; h: number; }) => {
    createElement({
      type: 'latex',
      id: nanoid(10),
      width: data.w,
      height: data.h,
      rotate: 0,
      left: (VIEWPORT_SIZE - data.w) / 2,
      top: (VIEWPORT_SIZE * viewportRatio.value - data.h) / 2,
      path: data.path,
      latex: data.latex,
      color: theme.value.fontColor,
      strokeWidth: 2,
      viewBox: [data.w, data.h],
      fixedRatio: true,
    })
  }

  /**
   * Create a video element
   * @param src video address
   */
  const createVideoElement = (src: string) => {
    createElement({
      type: 'video',
      id: nanoid(10),
      width: 500,
      height: 300,
      rotate: 0,
      left: (VIEWPORT_SIZE - 500) / 2,
      top: (VIEWPORT_SIZE * viewportRatio.value - 300) / 2,
      src,
    })
  }

  /**
   * Create audio elements
   * @param src audio address
   */
  const createAudioElement = (src: string) => {
    createElement({
      type: 'audio',
      id: nanoid(10),
      width: 50,
      height: 50,
      rotate: 0,
      left: (VIEWPORT_SIZE - 50) / 2,
      top: (VIEWPORT_SIZE * viewportRatio.value - 50) / 2,
      loop: false,
      autoplay: false,
      fixedRatio: true,
      color: theme.value.themeColor,
      src,
    })
  }

  return {
    createElement,
    createImageElement,
    createSectionElement,
    createTableElement,
    createTextElement,
    createHtmlElement,
    createDragDropElement,
    createSlideLayoutElement,
    createShapeElement,
    createLineElement,
    createLatexElement,
    createVideoElement,
    createAudioElement
  }
}
